import type { ControllerFetchOptions, IExaleadParams, IPagineableParams } from '~/features-shared/content/api/interfaces/params'
import { ORDERFIELD } from '~/features-shared/content/api/interfaces/params'
import type { IEntity } from '~/features-shared/libs/interfaces/entity'

import type { ExaleadService, SwymService } from '~/interfaces/crud-service'

export class ServiceFetchControllerDefault<T extends IEntity> {
  readonly serviceSwym: SwymService<T>
  readonly serviceExalead: ExaleadService<T>

  constructor(ServiceSwym: SwymService<T>, ServiceExalead: ExaleadService<T>) {
    this.serviceSwym = ServiceSwym
    this.serviceExalead = ServiceExalead
  }

  shouldFetchFromExalead(options: ControllerFetchOptions) {
    if (
      options.forceExalead
      || options.refine?.length
      || options.sort === ORDERFIELD.UPDATE
      || options.query
      || options.refineQuery)
      return true

    return false
  }

  shouldFetchTags(options: Pick<ControllerFetchOptions, 'page'>) {
    if (options.page <= 1)
      return true
    return false
  }

  // eslint-disable-next-line complexity
  getExaleadParams(options: ControllerFetchOptions): IExaleadParams {
    const page = options.page || 1
    const limit = options.limit || 10

    return {
      // user query
      query: options.query || '#all',
      // nresults = limit (number of objects to fetch)
      nresults: limit,
      // start = page.
      // If limit = 10 and page = 3, then start = 20 -> will return objects from index 20 to 30
      start: (page - 1) * limit,
      // community filter
      community_id: (options.defaultParams?.community_id as string ?? '').split(':')
        .pop() || undefined,
      // refine = tags selected from taggers + default
      refine: [...(options.defaultRefine || []), ...(options.refine || [])],
      // refineUsingQuery = programmatically setted query
      refineUsingQuery: [
        options.refineQuery,
        ...(options.defaultQuery || []),
      ].filter(e => e)
        .join(' AND ') || undefined,
      // withDraft = should includes drafts
      withDraft: options.withDraft || false,
      // order_field = Order by creation or latest activity.
      order_field: options.sort || ORDERFIELD.CREATION,
      // for derived types
      ...(options.withSubTypes ? { with_subtype: options.withSubTypes } : undefined),
      ...(options.derivedTypeId ? { derived_type_id: options.derivedTypeId } : undefined),
      ...(options.onlyFavoriteCommunities ? { onlyFavoriteCommunities: options.onlyFavoriteCommunities } : undefined),
    }
  }

  getSwymParams(options: ControllerFetchOptions): IPagineableParams & ControllerFetchOptions['defaultParams'] {
    return {
      ...options.defaultParams,
      ...(options.derivedTypeId ? { derivedTypeId: options.derivedTypeId } : undefined),
      limit: options.limit,
      page: options.page,
      ...(options.onlyFavoriteCommunities ? { onlyFavoriteCommunities: options.onlyFavoriteCommunities } : undefined),
    }
  }

  fetch(options: ControllerFetchOptions) {
    if (this.shouldFetchFromExalead(options))
      return this.serviceExalead.getAll(this.getExaleadParams(options))
    else
      return this.serviceSwym.getAll(this.getSwymParams(options))
  }

  fetchTags(options: Omit<ControllerFetchOptions, 'limit'>) {
    if (this.shouldFetchTags(options))
      return this.serviceExalead?.getRawData?.(this.getExaleadParams(options as ControllerFetchOptions))
  }

  count(options: Omit<ControllerFetchOptions, 'limit' | 'page'>) {
    return this.fetchTags({ ...options, page: 1 })
  }
}
